import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { 
  MessageCircle, Send, Search, Filter, User, Clock, Check, 
  CheckCheck, AlertCircle, Megaphone, Settings, Mail, RefreshCw,
  ChevronRight, X, Plus
} from 'lucide-react';

export default function AdminSupport() {
  const { token, user } = useAuth();
  const { currentColors } = useTheme();
  const [activeTab, setActiveTab] = useState('conversations');
  const [conversations, setConversations] = useState([]);
  const [stats, setStats] = useState({});
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Broadcast
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [broadcastTitle, setBroadcastTitle] = useState('');
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [broadcastRoles, setBroadcastRoles] = useState(['user']);
  
  // Contact settings
  const [contactSettings, setContactSettings] = useState({
    notification_email: '',
    auto_reply_enabled: false,
    auto_reply_message: ''
  });
  const [contactSubmissions, setContactSubmissions] = useState([]);
  const [contactStats, setContactStats] = useState({});
  
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [convRes, settingsRes, submissionsRes] = await Promise.all([
        axios.get(`${API}/admin/support/conversations${statusFilter ? `?status=${statusFilter}` : ''}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/contact/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/contact/submissions`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setConversations(convRes.data.conversations || []);
      setStats(convRes.data.stats || {});
      setContactSettings(settingsRes.data || {});
      setContactSubmissions(submissionsRes.data.submissions || []);
      setContactStats(submissionsRes.data.stats || {});
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (conversationId) => {
    try {
      const response = await axios.get(`${API}/support/conversations/${conversationId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.messages || []);
      setSelectedConversation(response.data.conversation);
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    const messageText = newMessage;
    setNewMessage('');
    
    try {
      await axios.post(
        `${API}/support/conversations/${selectedConversation.id}/messages`,
        { message: messageText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadMessages(selectedConversation.id);
      loadData();
    } catch (error) {
      console.error('Failed to send message:', error);
      setNewMessage(messageText);
    }
  };

  const updateConversationStatus = async (conversationId, status) => {
    try {
      await axios.put(
        `${API}/admin/support/conversations/${conversationId}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadData();
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation({ ...selectedConversation, status });
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const sendBroadcast = async () => {
    if (!broadcastTitle.trim() || !broadcastMessage.trim()) return;
    
    try {
      await axios.post(
        `${API}/admin/broadcasts`,
        { title: broadcastTitle, message: broadcastMessage, target_roles: broadcastRoles },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowBroadcastModal(false);
      setBroadcastTitle('');
      setBroadcastMessage('');
      setBroadcastRoles(['user']);
      alert('Broadcast sent successfully!');
    } catch (error) {
      console.error('Failed to send broadcast:', error);
      alert('Failed to send broadcast');
    }
  };

  const saveContactSettings = async () => {
    try {
      await axios.put(
        `${API}/admin/contact/settings`,
        contactSettings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      alert('Failed to save settings');
    }
  };

  const updateSubmissionStatus = async (submissionId, status) => {
    try {
      await axios.put(
        `${API}/admin/contact/submissions/${submissionId}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadData();
    } catch (error) {
      console.error('Failed to update submission:', error);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const filteredConversations = conversations.filter(conv => 
    !searchQuery || 
    conv.user_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conv.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conv.user_email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const tabs = [
    { id: 'conversations', label: 'Support Inbox', icon: MessageCircle, count: stats.open + stats.in_progress },
    { id: 'broadcasts', label: 'Broadcasts', icon: Megaphone },
    { id: 'contact', label: 'Contact Submissions', icon: Mail, count: contactStats.new },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  return (
    <div className="space-y-6" data-testid="admin-support">
      {/* Header */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-cinzel flex items-center gap-3" style={{ color: currentColors.primary }}>
              <MessageCircle className="w-8 h-8" />
              Support Center
            </h1>
            <p style={{ color: currentColors.textMuted }}>Manage user conversations, broadcasts, and contact form</p>
          </div>
          <button
            onClick={() => setShowBroadcastModal(true)}
            className="px-4 py-2 rounded-xl flex items-center gap-2"
            style={{ backgroundColor: currentColors.primary, color: 'white' }}
          >
            <Megaphone className="w-5 h-5" />
            New Broadcast
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Open', value: stats.open || 0, color: '#fbbf24' },
          { label: 'In Progress', value: stats.in_progress || 0, color: '#3b82f6' },
          { label: 'Resolved', value: stats.resolved || 0, color: '#22c55e' },
          { label: 'New Contacts', value: contactStats.new || 0, color: '#ec4899' }
        ].map(stat => (
          <div key={stat.label} className="glass-card p-4">
            <p className="text-sm" style={{ color: currentColors.textMuted }}>{stat.label}</p>
            <p className="text-3xl font-bold" style={{ color: stat.color }}>{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-xl flex items-center gap-2 whitespace-nowrap transition-all ${
              activeTab === tab.id ? 'scale-105' : 'opacity-70'
            }`}
            style={{ 
              backgroundColor: activeTab === tab.id ? `${currentColors.primary}20` : 'transparent',
              color: activeTab === tab.id ? currentColors.primary : currentColors.text,
              border: `1px solid ${activeTab === tab.id ? currentColors.primary : currentColors.border}`
            }}
          >
            <tab.icon className="w-5 h-5" />
            {tab.label}
            {tab.count > 0 && (
              <span 
                className="px-2 py-0.5 rounded-full text-xs text-white"
                style={{ backgroundColor: currentColors.primary }}
              >
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Conversations Tab */}
      {activeTab === 'conversations' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Conversation List */}
          <div className="lg:col-span-1 space-y-4">
            {/* Search & Filter */}
            <div className="glass-card p-4 space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: currentColors.textMuted }} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search conversations..."
                  className="w-full pl-10 pr-3 py-2 rounded-lg cosmic-input"
                />
              </div>
              <div className="flex gap-2">
                {['', 'open', 'in_progress', 'resolved'].map(status => (
                  <button
                    key={status || 'all'}
                    onClick={() => setStatusFilter(status)}
                    className={`px-3 py-1 rounded-lg text-xs ${statusFilter === status ? 'font-bold' : ''}`}
                    style={{ 
                      backgroundColor: statusFilter === status ? `${currentColors.primary}20` : 'transparent',
                      color: currentColors.text
                    }}
                  >
                    {status || 'All'}
                  </button>
                ))}
              </div>
            </div>

            {/* List */}
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {loading ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="w-8 h-8 animate-spin" style={{ color: currentColors.primary }} />
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="text-center py-8 glass-card">
                  <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p style={{ color: currentColors.textMuted }}>No conversations found</p>
                </div>
              ) : (
                filteredConversations.map(conv => (
                  <button
                    key={conv.id}
                    onClick={() => loadMessages(conv.id)}
                    className={`w-full p-3 rounded-xl text-left transition-all hover:scale-[1.02] ${
                      selectedConversation?.id === conv.id ? 'ring-2' : ''
                    }`}
                    style={{ 
                      backgroundColor: conv.admin_unread_count > 0 ? `${currentColors.primary}15` : currentColors.cardBg,
                      border: `1px solid ${currentColors.border}`,
                      ringColor: currentColors.primary
                    }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate" style={{ color: currentColors.text }}>
                          {conv.user_name}
                        </p>
                        <p className="text-xs" style={{ color: currentColors.textMuted }}>
                          {conv.user_email}
                        </p>
                        <p className="text-sm mt-1 truncate" style={{ color: currentColors.text }}>
                          {conv.subject}
                        </p>
                      </div>
                      {conv.admin_unread_count > 0 && (
                        <span 
                          className="w-5 h-5 rounded-full text-xs text-white flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: currentColors.primary }}
                        >
                          {conv.admin_unread_count}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <span 
                        className="text-xs px-2 py-0.5 rounded-full"
                        style={{ 
                          backgroundColor: conv.status === 'resolved' ? 'rgba(34, 197, 94, 0.2)' : 
                                         conv.status === 'in_progress' ? 'rgba(59, 130, 246, 0.2)' : 
                                         'rgba(251, 191, 36, 0.2)',
                          color: conv.status === 'resolved' ? '#22c55e' : 
                                conv.status === 'in_progress' ? '#3b82f6' : '#fbbf24'
                        }}
                      >
                        {conv.status.replace('_', ' ')}
                      </span>
                      <span className="text-xs" style={{ color: currentColors.textMuted }}>
                        {new Date(conv.updated_at).toLocaleDateString()}
                      </span>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Chat Panel */}
          <div className="lg:col-span-2 glass-card flex flex-col h-[600px]">
            {selectedConversation ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b" style={{ borderColor: currentColors.border }}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold" style={{ color: currentColors.text }}>
                        {selectedConversation.subject}
                      </h3>
                      <p className="text-sm" style={{ color: currentColors.textMuted }}>
                        {selectedConversation.user_name} ({selectedConversation.user_email})
                      </p>
                    </div>
                    <select
                      value={selectedConversation.status}
                      onChange={(e) => updateConversationStatus(selectedConversation.id, e.target.value)}
                      className="px-3 py-1 rounded-lg text-sm cosmic-input"
                    >
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {messages.map(msg => (
                    <div 
                      key={msg.id}
                      className={`flex ${msg.sender_role === 'admin' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div 
                        className={`max-w-[70%] p-3 rounded-2xl ${msg.sender_role === 'admin' ? 'rounded-tr-md' : 'rounded-tl-md'}`}
                        style={{ 
                          backgroundColor: msg.sender_role === 'admin' ? `${currentColors.primary}20` : `${currentColors.secondary}20`,
                          border: `1px solid ${msg.sender_role === 'admin' ? currentColors.primary : currentColors.secondary}30`
                        }}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <User className="w-3 h-3" style={{ color: currentColors.textMuted }} />
                          <span className="text-xs font-medium" style={{ color: currentColors.textMuted }}>
                            {msg.sender_name} {msg.sender_role === 'admin' && '(Admin)'}
                          </span>
                        </div>
                        <p className="text-sm" style={{ color: currentColors.text }}>
                          {msg.message}
                        </p>
                        <p className="text-[10px] mt-1 text-right" style={{ color: currentColors.textMuted }}>
                          {formatTime(msg.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="p-4 border-t" style={{ borderColor: currentColors.border }}>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                      placeholder="Type your reply..."
                      className="flex-1 px-4 py-2 rounded-xl cosmic-input"
                    />
                    <button
                      onClick={sendMessage}
                      disabled={!newMessage.trim()}
                      className="px-4 py-2 rounded-xl disabled:opacity-50"
                      style={{ backgroundColor: currentColors.primary, color: 'white' }}
                    >
                      <Send className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-30" style={{ color: currentColors.textMuted }} />
                  <p style={{ color: currentColors.textMuted }}>Select a conversation to view messages</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Broadcasts Tab */}
      {activeTab === 'broadcasts' && (
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-cinzel" style={{ color: currentColors.text }}>Send Announcements</h2>
          </div>
          <div className="max-w-2xl space-y-4">
            <div>
              <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Title</label>
              <input
                type="text"
                value={broadcastTitle}
                onChange={(e) => setBroadcastTitle(e.target.value)}
                placeholder="Announcement title..."
                className="w-full cosmic-input"
              />
            </div>
            <div>
              <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Message</label>
              <textarea
                value={broadcastMessage}
                onChange={(e) => setBroadcastMessage(e.target.value)}
                placeholder="Write your announcement..."
                rows={4}
                className="w-full cosmic-input resize-none"
              />
            </div>
            <div>
              <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Target Roles</label>
              <div className="flex flex-wrap gap-2">
                {['user', 'practitioner', 'content_admin', 'user_admin', 'super_admin'].map(role => (
                  <label key={role} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={broadcastRoles.includes(role)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setBroadcastRoles([...broadcastRoles, role]);
                        } else {
                          setBroadcastRoles(broadcastRoles.filter(r => r !== role));
                        }
                      }}
                      className="rounded"
                    />
                    <span className="text-sm" style={{ color: currentColors.text }}>{role.replace('_', ' ')}</span>
                  </label>
                ))}
              </div>
            </div>
            <button
              onClick={sendBroadcast}
              disabled={!broadcastTitle.trim() || !broadcastMessage.trim()}
              className="px-6 py-2 rounded-xl disabled:opacity-50"
              style={{ backgroundColor: currentColors.primary, color: 'white' }}
            >
              <Megaphone className="w-5 h-5 inline mr-2" />
              Send Broadcast
            </button>
          </div>
        </div>
      )}

      {/* Contact Submissions Tab */}
      {activeTab === 'contact' && (
        <div className="glass-card p-6">
          <h2 className="text-xl font-cinzel mb-6" style={{ color: currentColors.text }}>Contact Form Submissions</h2>
          <div className="space-y-4">
            {contactSubmissions.length === 0 ? (
              <div className="text-center py-8">
                <Mail className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p style={{ color: currentColors.textMuted }}>No submissions yet</p>
              </div>
            ) : (
              contactSubmissions.map(sub => (
                <div 
                  key={sub.id}
                  className="p-4 rounded-xl"
                  style={{ 
                    backgroundColor: sub.status === 'new' ? `${currentColors.primary}10` : 'transparent',
                    border: `1px solid ${currentColors.border}`
                  }}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium" style={{ color: currentColors.text }}>{sub.name}</span>
                        <span className="text-sm" style={{ color: currentColors.textMuted }}>({sub.email})</span>
                      </div>
                      <p className="font-medium" style={{ color: currentColors.primary }}>{sub.subject}</p>
                      <p className="text-sm mt-2" style={{ color: currentColors.text }}>{sub.message}</p>
                      <p className="text-xs mt-2" style={{ color: currentColors.textMuted }}>
                        {formatTime(sub.created_at)}
                      </p>
                    </div>
                    <select
                      value={sub.status}
                      onChange={(e) => updateSubmissionStatus(sub.id, e.target.value)}
                      className="px-3 py-1 rounded-lg text-sm cosmic-input"
                    >
                      <option value="new">New</option>
                      <option value="read">Read</option>
                      <option value="replied">Replied</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="glass-card p-6">
          <h2 className="text-xl font-cinzel mb-6" style={{ color: currentColors.text }}>Contact Form Settings</h2>
          <div className="max-w-xl space-y-4">
            <div>
              <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>
                Notification Email
              </label>
              <input
                type="email"
                value={contactSettings.notification_email || ''}
                onChange={(e) => setContactSettings({ ...contactSettings, notification_email: e.target.value })}
                placeholder="admin@example.com"
                className="w-full cosmic-input"
              />
              <p className="text-xs mt-1" style={{ color: currentColors.textMuted }}>
                Receive email notifications for new contact form submissions and support requests
              </p>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="auto_reply"
                checked={contactSettings.auto_reply_enabled || false}
                onChange={(e) => setContactSettings({ ...contactSettings, auto_reply_enabled: e.target.checked })}
                className="rounded"
              />
              <label htmlFor="auto_reply" style={{ color: currentColors.text }}>
                Enable auto-reply for contact form
              </label>
            </div>
            {contactSettings.auto_reply_enabled && (
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>
                  Auto-reply Message
                </label>
                <textarea
                  value={contactSettings.auto_reply_message || ''}
                  onChange={(e) => setContactSettings({ ...contactSettings, auto_reply_message: e.target.value })}
                  placeholder="Thank you for contacting us..."
                  rows={3}
                  className="w-full cosmic-input resize-none"
                />
              </div>
            )}
            <button
              onClick={saveContactSettings}
              className="px-6 py-2 rounded-xl"
              style={{ backgroundColor: currentColors.primary, color: 'white' }}
            >
              Save Settings
            </button>
          </div>
        </div>
      )}

      {/* Broadcast Modal */}
      {showBroadcastModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div 
            className="w-full max-w-lg rounded-2xl p-6"
            style={{ backgroundColor: currentColors.cardBg }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-cinzel" style={{ color: currentColors.text }}>New Broadcast</h3>
              <button onClick={() => setShowBroadcastModal(false)}>
                <X className="w-6 h-6" style={{ color: currentColors.textMuted }} />
              </button>
            </div>
            <div className="space-y-4">
              <input
                type="text"
                value={broadcastTitle}
                onChange={(e) => setBroadcastTitle(e.target.value)}
                placeholder="Title"
                className="w-full cosmic-input"
              />
              <textarea
                value={broadcastMessage}
                onChange={(e) => setBroadcastMessage(e.target.value)}
                placeholder="Message"
                rows={4}
                className="w-full cosmic-input resize-none"
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowBroadcastModal(false)}
                  className="px-4 py-2 rounded-xl"
                  style={{ border: `1px solid ${currentColors.border}`, color: currentColors.text }}
                >
                  Cancel
                </button>
                <button
                  onClick={sendBroadcast}
                  className="px-4 py-2 rounded-xl"
                  style={{ backgroundColor: currentColors.primary, color: 'white' }}
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
